/**
 * <s-ship-countdown> — Same-day shipping countdown in the store's time zone.
 *
 * Attributes:
 *   data-timezone     — IANA zone for the cutoff (e.g. America/New_York).
 *   data-cutoff-hour  — Cutoff hour, 24h clock (e.g. 17).
 *   data-ship-days    — Comma list of shipping weekdays, 0=Sun … 6=Sat (e.g. "1,2,3,4,5").
 *   data-before       — Template before cutoff; {time} is replaced with the countdown.
 *   data-after        — Template otherwise; {day} is replaced with the next ship day.
 *   data-tomorrow     — Word used when the next ship day is tomorrow.
 *   data-transit-min  — Fewest business days in transit after shipping (e.g. 2).
 *   data-transit-max  — Most business days in transit after shipping (e.g. 5).
 *   data-delivery     — Template for the estimate; {range} is replaced with the delivery dates.
 *   data-transit-states — JSON {"OH":[1,1],...}: business days in transit per US state.
 *   data-transit-zones  — JSON fallback ranges by browser time zone.
 *   data-delivery-to    — Template when the state is known; {place} and {range} are replaced.
 * Children: [data-delivery-text] (optional) receives the estimated delivery range;
 * [data-locate] button (command="--locate") asks for the device location.
 * [data-countdown-text] receives the rendered message (HTML-free; {time}
 * renders inside a <strong>).
 */
const WEEKDAYS = { Sun: 0, Mon: 1, Tue: 2, Wed: 3, Thu: 4, Fri: 5, Sat: 6 };
const STATE_KEY = 'distvnt:ship-state';

/** US states: [name, approximate center latitude, longitude], used to turn a device location into a state. */
const STATES = {
  AL: ['Alabama', 32.8, -86.8], AK: ['Alaska', 61.4, -150.0], AZ: ['Arizona', 34.3, -111.7], AR: ['Arkansas', 34.9, -92.4],
  CA: ['California', 37.2, -119.5], CO: ['Colorado', 39.0, -105.5], CT: ['Connecticut', 41.6, -72.7], DE: ['Delaware', 39.0, -75.5],
  DC: ['Washington, DC', 38.9, -77.0], FL: ['Florida', 28.6, -82.4], GA: ['Georgia', 32.7, -83.4], HI: ['Hawaii', 20.8, -156.3],
  ID: ['Idaho', 44.4, -114.6], IL: ['Illinois', 40.0, -89.2], IN: ['Indiana', 39.9, -86.3], IA: ['Iowa', 42.1, -93.5],
  KS: ['Kansas', 38.5, -98.4], KY: ['Kentucky', 37.5, -85.3], LA: ['Louisiana', 31.1, -92.0], ME: ['Maine', 45.4, -69.2],
  MD: ['Maryland', 39.0, -76.8], MA: ['Massachusetts', 42.3, -71.8], MI: ['Michigan', 43.6, -84.7], MN: ['Minnesota', 46.3, -94.3],
  MS: ['Mississippi', 32.7, -89.7], MO: ['Missouri', 38.4, -92.5], MT: ['Montana', 47.0, -109.6], NE: ['Nebraska', 41.5, -99.8],
  NV: ['Nevada', 39.3, -116.6], NH: ['New Hampshire', 43.7, -71.6], NJ: ['New Jersey', 40.2, -74.7], NM: ['New Mexico', 34.4, -106.1],
  NY: ['New York', 42.9, -75.5], NC: ['North Carolina', 35.6, -79.4], ND: ['North Dakota', 47.5, -100.5], OH: ['Ohio', 40.3, -82.8],
  OK: ['Oklahoma', 35.6, -97.5], OR: ['Oregon', 43.9, -120.6], PA: ['Pennsylvania', 40.9, -77.8], RI: ['Rhode Island', 41.7, -71.5],
  SC: ['South Carolina', 33.9, -80.9], SD: ['South Dakota', 44.4, -100.2], TN: ['Tennessee', 35.9, -86.4], TX: ['Texas', 31.5, -99.3],
  UT: ['Utah', 39.3, -111.7], VT: ['Vermont', 44.1, -72.7], VA: ['Virginia', 37.5, -78.9], WA: ['Washington', 47.4, -120.5],
  WV: ['West Virginia', 38.6, -80.6], WI: ['Wisconsin', 44.6, -89.9], WY: ['Wyoming', 43.0, -107.6],
};

/** Extra reference points (major cities, odd-shaped states) so shoppers near a border get the right state. */
const CITY_POINTS = [
  ['NY', 40.71, -74.0], ['NY', 40.75, -73.4], ['NY', 42.89, -78.88], ['NY', 43.16, -77.61], ['MI', 42.33, -83.05], ['MI', 42.96, -85.67],
  ['MI', 46.5, -87.4], ['MI', 41.95, -86.4], ['OH', 41.5, -81.69], ['OH', 41.65, -83.54], ['OH', 39.1, -84.51], ['IL', 41.88, -87.63],
  ['IN', 41.6, -87.3], ['IN', 39.77, -86.16], ['PA', 39.95, -75.17], ['PA', 40.44, -79.99], ['NJ', 40.74, -74.17], ['NJ', 39.36, -74.42],
  ['CA', 34.05, -118.24], ['CA', 37.77, -122.42], ['CA', 32.72, -117.16], ['TX', 29.76, -95.37], ['TX', 32.78, -96.8], ['TX', 31.76, -106.49],
  ['MA', 42.36, -71.06], ['MD', 39.29, -76.61], ['VA', 38.85, -77.3], ['VA', 36.85, -76.29], ['DC', 38.9, -77.03], ['MO', 38.63, -90.2],
  ['MO', 39.1, -94.58], ['KS', 39.1, -94.7], ['KY', 38.25, -85.76], ['KY', 39.05, -84.5], ['WI', 43.04, -87.91], ['MN', 44.98, -93.27],
  ['TN', 35.15, -90.05], ['TN', 36.16, -86.78], ['GA', 33.75, -84.39], ['FL', 30.44, -84.28], ['FL', 30.33, -81.66], ['FL', 25.76, -80.19],
  ['WA', 47.61, -122.33], ['OR', 45.52, -122.68], ['CO', 39.74, -104.99], ['NV', 36.17, -115.14], ['AZ', 33.45, -112.07], ['UT', 40.76, -111.89],
  ['LA', 29.95, -90.07], ['NC', 35.23, -80.84], ['SC', 32.78, -79.93], ['CT', 41.31, -72.92], ['RI', 41.82, -71.41], ['DE', 39.74, -75.55],
  ['WV', 38.35, -81.63], ['ID', 47.68, -116.78],
];


/** Time zones that pin down a single state (prefix match), so some shoppers get an exact estimate with no prompt. */
const ZONE_STATES = {
  'America/Detroit': 'MI', 'America/Menominee': 'MI', 'America/Indiana': 'IN', 'America/Kentucky': 'KY', 'America/Louisville': 'KY',
  'America/Boise': 'ID', 'America/Phoenix': 'AZ', 'America/Anchorage': 'AK', 'America/Juneau': 'AK', 'America/Nome': 'AK',
  'America/Sitka': 'AK', 'America/Yakutat': 'AK', 'Pacific/Honolulu': 'HI', 'America/North_Dakota': 'ND',
};

/** Nearest state center to a point, or null when the point is clearly outside the US. */
function nearestState(lat, lon) {
  const inLower48 = lat > 24 && lat < 49.5 && lon > -125 && lon < -66.5;
  const inAlaska = lat > 51 && lat < 72 && lon < -129;
  const inHawaii = lat > 18.5 && lat < 22.5 && lon > -161 && lon < -154;
  if (!inLower48 && !inAlaska && !inHawaii) return null;
  let best = null;
  let bestDistance = Infinity;
  const points = [...Object.entries(STATES).map(([code, [, sLat, sLon]]) => [code, sLat, sLon]), ...CITY_POINTS];
  for (const [code, sLat, sLon] of points) {
    const dLat = lat - sLat;
    const dLon = (lon - sLon) * Math.cos((lat * Math.PI) / 180);
    const distance = dLat * dLat + dLon * dLon;
    if (distance < bestDistance) { bestDistance = distance; best = code; }
  }
  return best;
}

class SShipCountdown extends HTMLElement {
  #timer = 0;
  #today = null;

  /**
   * The calendar date `offset` days after today in the store's time zone, as a UTC-noon Date
   * (format it with timeZone 'UTC'). Counting whole calendar days instead of 24-hour blocks
   * keeps the dates right across daylight saving changes.
   */
  #dateAfter(offset) {
    const { year, month, day } = this.#today;
    return new Date(Date.UTC(year, month - 1, day + offset, 12));
  }

  connectedCallback() {
    this.#render();
    this.#timer = window.setInterval(() => this.#render(), 1000);
    // "Use my location" button: commandfor="<this id>" command="--locate"; a click listener covers browsers without invoker commands.
    this.onCommand = (event) => { if (event.command === '--locate') this.#locate(); };
    this.addEventListener('command', this.onCommand);
    this.locateButton = this.querySelector('[data-locate]');
    if (this.locateButton && !('commandForElement' in HTMLButtonElement.prototype)) {
      this.onLocateClick = () => this.#locate();
      this.locateButton.addEventListener('click', this.onLocateClick);
    }
    this.#autoLocate();
  }

  disconnectedCallback() {
    window.clearInterval(this.#timer);
    this.removeEventListener('command', this.onCommand);
    this.locateButton?.removeEventListener('click', this.onLocateClick);
  }

  #nowParts() {
    const parts = new Intl.DateTimeFormat('en-US', {
      timeZone: this.dataset.timezone || undefined,
      weekday: 'short', year: 'numeric', month: 'numeric', day: 'numeric', hour: 'numeric', minute: 'numeric', second: 'numeric', hourCycle: 'h23',
    }).formatToParts(new Date());
    const get = (type) => parts.find((p) => p.type === type)?.value;
    return {
      weekday: WEEKDAYS[get('weekday')] ?? 0,
      year: Number(get('year')),
      month: Number(get('month')),
      day: Number(get('day')),
      seconds: Number(get('hour')) * 3600 + Number(get('minute')) * 60 + Number(get('second')),
    };
  }

  #render() {
    const target = this.querySelector('[data-countdown-text]');
    if (!target) return;
    const shipDays = (this.dataset.shipDays || '1,2,3,4,5').split(',').map(Number);
    const cutoff = Number(this.dataset.cutoffHour || 17) * 3600;
    const now = this.#nowParts();
    const { weekday, seconds } = now;
    this.#today = now;

    const shipsToday = shipDays.includes(weekday) && seconds < cutoff;
    let shipOffset = 0;
    if (!shipsToday) {
      shipOffset = 1;
      while (shipOffset <= 7 && !shipDays.includes((weekday + shipOffset) % 7)) shipOffset += 1;
    }
    this.#renderDelivery(weekday, shipOffset);

    if (shipsToday) {
      const left = cutoff - seconds;
      const h = Math.floor(left / 3600);
      const m = Math.floor((left % 3600) / 60);
      const s = left % 60;
      const time = `${h}h ${String(m).padStart(2, '0')}m ${String(s).padStart(2, '0')}s`;
      const [before, after = ''] = (this.dataset.before || '{time}').split('{time}');
      const strong = document.createElement('strong');
      strong.className = 'font-semibold text-on-surface tabular-nums';
      strong.textContent = time;
      target.replaceChildren(before, strong, after);
      this.toggleAttribute('data-open', true);
      return;
    }

    let offset = 1;
    while (offset <= 7 && !shipDays.includes((weekday + offset) % 7)) offset += 1;
    let day = this.dataset.tomorrow || 'tomorrow';
    if (offset > 1) {
      day = new Intl.DateTimeFormat(document.documentElement.lang || undefined, {
        timeZone: 'UTC', weekday: 'long',
      }).format(this.#dateAfter(offset));
    }
    target.textContent = (this.dataset.after || '{day}').replace('{day}', day);
    this.toggleAttribute('data-open', false);
  }

  /**
   * Where the shopper is, best source first:
   * 1. A signed-in customer's default address (data-customer-province; "none" = outside the US).
   * 2. A state saved on this device after the shopper tapped "Use my location".
   * 3. A state implied by the browser time zone (e.g. America/Detroit = Michigan). No prompt.
   * Returns { state, zone, source }.
   */
  #shopperLocation() {
    const province = (this.dataset.customerProvince || '').toUpperCase();
    if (province) return { state: province === 'NONE' ? null : province, source: 'address' };
    let saved = null;
    try { saved = localStorage.getItem(STATE_KEY); } catch {}
    if (saved && STATES[saved]) return { state: saved, source: 'location' };
    let zone = '';
    try { zone = Intl.DateTimeFormat().resolvedOptions().timeZone || ''; } catch {}
    const zoneState = Object.entries(ZONE_STATES).find(([prefix]) => zone.startsWith(prefix))?.[1] ?? null;
    return { state: zoneState, zone, source: zoneState ? 'timezone' : 'unknown' };
  }

  #json(name) {
    try { return JSON.parse(this.dataset[name] || '{}'); } catch { return {}; }
  }

  /** Transit business days [min, max] for the shopper: per state, else per time zone, else the default range. */
  #transit(location) {
    const states = this.#json('transitStates');
    if (location.state && states[location.state]) return states[location.state];
    const zones = this.#json('transitZones');
    if (location.zone && zones[location.zone]) return zones[location.zone];
    return [Number(this.dataset.transitMin || 2), Number(this.dataset.transitMax || 4)];
  }

  /** "Use my location": asks the browser for the shopper's position once, maps it to the nearest US state, and remembers it on this device. */
  #locate() {
    if (!navigator.geolocation || this.hasAttribute('data-locating')) return;
    this.toggleAttribute('data-locating', true);
    navigator.geolocation.getCurrentPosition(
      ({ coords }) => {
        this.toggleAttribute('data-locating', false);
        const state = nearestState(coords.latitude, coords.longitude);
        if (state) {
          try { localStorage.setItem(STATE_KEY, state); } catch {}
          this.toggleAttribute('data-locate-failed', false);
        } else {
          this.toggleAttribute('data-locate-failed', true);
        }
        this.#render();
      },
      () => {
        this.toggleAttribute('data-locating', false);
        this.toggleAttribute('data-locate-failed', true);
      },
      { enableHighAccuracy: false, timeout: 10000, maximumAge: 86400000 },
    );
  }

  /** Uses the shopper's location without a prompt when they've already allowed it for this store. */
  async #autoLocate() {
    if (this.#shopperLocation().source === 'address' || !navigator.permissions?.query) return;
    try {
      const status = await navigator.permissions.query({ name: 'geolocation' });
      if (status.state === 'granted') this.#locate();
    } catch {}
  }

  /** Estimated delivery: ship day plus transit business days (Monday to Friday). */
  #renderDelivery(weekday, shipOffset) {
    const target = this.querySelector('[data-delivery-text]');
    if (!target) return;
    const location = this.#shopperLocation();
    const [min, max] = this.#transit(location).map(Number);
    const local = !!location.state && location.state === (this.dataset.localProvince || '').toUpperCase();
    this.toggleAttribute('data-local', local);
    this.toggleAttribute('data-located', location.source === 'address' || location.source === 'location');
    this.toggleAttribute('data-can-locate', 'geolocation' in navigator && location.source !== 'address');
    const addBusinessDays = (startOffset, count) => {
      let offset = startOffset;
      let added = 0;
      while (added < count) {
        offset += 1;
        const day = (weekday + offset) % 7;
        if (day !== 0 && day !== 6) added += 1; // Deliveries never land on a weekend (UPS Ground: no Sundays).
      }
      return offset;
    };
    const format = new Intl.DateTimeFormat(document.documentElement.lang || undefined, {
      timeZone: 'UTC', weekday: 'short', month: 'short', day: 'numeric',
    });
    const dateAt = (offset) => format.format(this.#dateAfter(offset));
    const from = dateAt(addBusinessDays(shipOffset, min));
    const to = dateAt(addBusinessDays(shipOffset, max));
    const range = from === to ? from : `${from} – ${to}`;
    const place = location.state && location.source !== 'timezone' ? STATES[location.state]?.[0] : '';
    const template = place ? (this.dataset.deliveryTo || '{range}').replace('{place}', place) : (this.dataset.delivery || '{range}');
    const [before, after = ''] = template.split('{range}');
    const key = `${place}|${range}`;
    if (target.dataset.range === key) return;
    target.dataset.range = key;
    const strong = document.createElement('strong');
    strong.className = 'font-semibold text-on-surface';
    strong.textContent = range;
    target.replaceChildren(before, strong, after);
  }
}

if (!customElements.get('s-ship-countdown')) customElements.define('s-ship-countdown', SShipCountdown);
